import React, { useRef, useEffect, useCallback, useLayoutEffect, useState } from 'react';

interface TextExtractorProps {
    children?: React.ReactNode,
    onExtractedText: (text: string) => void,
}

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const TextExtractor = React.memo(function TextExtractor({ children, onExtractedText }: TextExtractorProps): React.ReactElement {
    const ref = useRef<HTMLSpanElement>(null);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const getText = useCallback((element: HTMLElement): string => {
        let text = '';

        const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_ALL,
            {
                acceptNode: (node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as Element;
                        // Check for nested __text-extractor spans
                        if (element.tagName.toLowerCase() === 'span' &&
                            element.classList.contains('__text-extractor')) {
                            // Check if this is a nested text-extractor by looking at ancestors
                            let current = node.parentNode;
                            let nestedDepth = 0;
                            while (current && current !== element) {
                                if (current.nodeType === Node.ELEMENT_NODE) {
                                    const currentElement = current as Element;
                                    if (currentElement.tagName.toLowerCase() === 'span' &&
                                        currentElement.classList.contains('__text-extractor')) {
                                        nestedDepth++;
                                    }
                                }
                                current = current.parentNode;
                            }

                            // Skip nested text-extractor elements (nestedDepth > 0)
                            if (nestedDepth > 0) {
                                return NodeFilter.FILTER_REJECT;
                            }
                        }
                    }

                    // Accept text nodes and other non-element nodes
                    return NodeFilter.FILTER_ACCEPT;
                }
            }
        );

        let node: Node | null;
        while ((node = walker.nextNode())) {
            if (node.nodeType === Node.TEXT_NODE) {
                const textContent = node.textContent?.trim() ?? '';
                if (textContent) {
                    text += textContent;
                }
            }
        }

        return text;
    }, []);

    const extractAndReport = useCallback(() => {
        if (ref.current) {
            const extractedText = getText(ref.current);
            onExtractedText(extractedText);
        }
    }, [getText, onExtractedText]);

    // Extract text after DOM updates
    useIsomorphicLayoutEffect(() => {
        if (isClient) {
            extractAndReport();
        }
    }, [children, isClient, extractAndReport]);

    // Also use MutationObserver to catch dynamic changes
    useEffect(() => {
        if (!isClient || !ref.current) return;

        const observer = new MutationObserver(() => {
            // Use a small delay to ensure React has finished updating
            setTimeout(extractAndReport, 0);
        });

        observer.observe(ref.current, {
            childList: true,
            subtree: true,
            characterData: true
        });

        return () => observer.disconnect();
    }, [isClient, extractAndReport]);

    return (
        <span className="__text-extractor" ref={ref} style={{ display: 'none' }} aria-hidden="true">
            {children}
        </span>
    );
});
export function findFirstComponentOfType(children: React.ReactNode, tagName: string, recurse: boolean = false): React.ReactElement | null {
    let result: React.ReactElement | null = null;

    React.Children.forEach(children, (child) => {
        if (result) return;

        if (React.isValidElement(child)) {
            if (child.type.toString().toLowerCase() === tagName.toLowerCase()) {
                result = child;
            } else if (child.props?.children && recurse) {
                result = findFirstComponentOfType(child.props.children, tagName, recurse);
            }
        }
    });

    return result;
}